import { z } from "zod"

import { entityIdSchema } from "./identifiers.js"
import { visitExecutionPlanProjectionV1Schema } from "./field-visit-execution-v1.js"
import {
  fieldProjectionV6Schema,
  fieldVisitProjectionV6Schema,
} from "./field-projection-v6.js"

export const fieldVisitExecutionPlanProjectionV2Schema = z
  .object({
    ...visitExecutionPlanProjectionV1Schema.shape,
    executionState: z.enum([
      "Ready",
      "En Route",
      "Arrived",
      "In Progress",
      "Completed",
      "Exception Review",
    ]),
    skipId: entityIdSchema.nullable(),
    skipReason: z.string().trim().min(1).max(2_000).nullable(),
    skippedAt: z.iso.datetime({ offset: true }).nullable(),
  })
  .strict()
  .superRefine((plan, context) => {
    const skipped = plan.skipId !== null
    if (
      skipped !== (plan.skippedAt !== null) ||
      (skipped && plan.executionState !== "Exception Review")
    ) {
      context.addIssue({
        code: "custom",
        message: "Skipped Visit state must be complete and terminal.",
        path: ["skipId"],
      })
    }
    if (!skipped && plan.skipReason !== null) {
      context.addIssue({
        code: "custom",
        message: "A Skip reason requires a Skip event.",
        path: ["skipReason"],
      })
    }
  })

export const fieldVisitProjectionV7Schema = z
  .object({
    ...fieldVisitProjectionV6Schema.shape,
    state: z.enum(["scheduled", "completed", "incomplete", "cancelled"]),
    executionPlan: fieldVisitExecutionPlanProjectionV2Schema,
  })
  .strict()
  .refine((visit) => Date.parse(visit.startsAt) < Date.parse(visit.endsAt), {
    message: "Field Visit must end after it starts.",
    path: ["endsAt"],
  })
  .superRefine((visit, context) => {
    if (
      visit.executionPlan.skipId !== null &&
      visit.state !== "incomplete"
    ) {
      context.addIssue({
        code: "custom",
        message: "A skipped Visit must be incomplete.",
        path: ["state"],
      })
    }
  })

export const fieldProjectionV7Schema = z
  .object({
    ...fieldProjectionV6Schema.shape,
    responseVersion: z.literal(7),
    visits: z.array(fieldVisitProjectionV7Schema).max(100),
  })
  .strict()
  .superRefine((projection, context) => {
    const generated = Date.parse(projection.generatedAt)
    const expires = Date.parse(projection.expiresAt)
    if (expires <= generated || expires - generated > 15 * 60 * 1_000) {
      context.addIssue({
        code: "custom",
        message: "Field projection expiry must be within 15 minutes.",
        path: ["expiresAt"],
      })
    }
    const start = Date.parse(`${projection.horizonStartDate}T00:00:00Z`)
    const end = Date.parse(`${projection.horizonEndDate}T00:00:00Z`)
    if (end - start !== 2 * 24 * 60 * 60 * 1_000) {
      context.addIssue({
        code: "custom",
        message: "Field projection horizon must be today and the next day.",
        path: ["horizonEndDate"],
      })
    }
    if (
      new Set(projection.visits.map((visit) => visit.visitId)).size !==
      projection.visits.length
    ) {
      context.addIssue({
        code: "custom",
        message: "Field projection Visits must be unique.",
        path: ["visits"],
      })
    }
    if (
      projection.workShift &&
      !projection.relationships.some(
        (relationship) =>
          relationship.relationshipId ===
            projection.workShift?.relationshipId &&
          relationship.legalEntityId === projection.workShift.legalEntityId,
      )
    ) {
      context.addIssue({
        code: "custom",
        message: "Active Work Shift must use a current relationship.",
        path: ["workShift"],
      })
    }
    if (
      projection.visitTime &&
      (!projection.workShift ||
        projection.visitTime.workShiftId !== projection.workShift.workShiftId)
    ) {
      context.addIssue({
        code: "custom",
        message: "Visit Time must be nested in the active Work Shift.",
        path: ["visitTime"],
      })
    }
    if (
      projection.visitTime &&
      !projection.relationships.some(
        (relationship) =>
          relationship.legalEntityId === projection.visitTime?.legalEntityId &&
          relationship.branchIds.includes(projection.visitTime.branchId),
      )
    ) {
      context.addIssue({
        code: "custom",
        message: "Visit Time must remain inside the current worker scope.",
        path: ["visitTime"],
      })
    }
    for (const [visitIndex, visit] of projection.visits.entries()) {
      const setIds = visit.beforeAfterSets.map((set) => set.setId)
      if (new Set(setIds).size !== setIds.length) {
        context.addIssue({
          code: "custom",
          message: "Before/After sets must be unique within a Visit.",
          path: ["visits", visitIndex, "beforeAfterSets"],
        })
      }
    }
  })

export type FieldProjectionV7 = z.infer<typeof fieldProjectionV7Schema>
export type FieldVisitProjectionV7 = z.infer<
  typeof fieldVisitProjectionV7Schema
>
